import { useState, useEffect } from 'react';
import { ShieldCheck, X, Timer, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ThreeDSChallengePopupProps {
  merchant: string;
  amount: string;
  challengeId?: string;
  onApprove: () => void;
  onDeny: () => void;
  onDismiss: () => void;
}

export function ThreeDSChallengePopup({ merchant, amount, challengeId, onApprove, onDeny, onDismiss }: ThreeDSChallengePopupProps) {
  const [secondsLeft, setSecondsLeft] = useState(180); // 3 min expiry

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          onDismiss();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onDismiss]);

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="bg-destructive/10 px-5 py-4 flex items-center gap-3 border-b border-border">
          <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-foreground">3D Secure Verification</h3>
            <p className="text-[11px] text-muted-foreground">PayNovus Authentication</p>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={onDismiss}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">
          <div className="text-center space-y-1">
            <p className="text-xs text-muted-foreground">Transaction at</p>
            <p className="text-sm font-semibold text-foreground">{merchant}</p>
            <p className="text-lg font-bold text-foreground">{amount}</p>
          </div>

          <div className="text-center space-y-2">
            <p className="text-xs text-muted-foreground">
              Do you recognise this transaction and wish to approve it?
            </p>
          </div>

          {/* Timer */}
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Timer className="w-3.5 h-3.5" />
            <span>
              Expires in {mins}:{secs.toString().padStart(2, '0')}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 text-destructive border-destructive/30 hover:bg-destructive/10"
              onClick={onDeny}
            >
              Deny
            </Button>
            <Button
              className="flex-1"
              onClick={onApprove}
            >
              Approve
            </Button>
          </div>

          {/* Open challenge link (placeholder for issuer ACS redirect) */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs gap-1.5 text-muted-foreground"
            onClick={onApprove}
          >
            <ExternalLink className="w-3 h-3" />
            Open full challenge page
          </Button>

          <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
            This is a 3D Secure (EMV 3DS 2.x) authentication request from your card issuer. 
            If you did not initiate this transaction, deny it immediately.
          </p>
        </div>
      </div>
    </div>
  );
}
